import { ChangeEvent, FunctionComponent, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { SmoLanguage, allSmoLanguages } from '../../../../Models/Smo/SmoLanguage';
import { SmoMoon } from '../../../../Models/Smo/SmoMoon';
import CreateMoonListActions from '../../../../Modules/Redux/Pages/CreateMoonList/CreateMoonListActions';
import CreateMoonListSelectors from '../../../../Modules/Redux/Pages/CreateMoonList/CreateMoonListSelectors';
import { DEFAULT_GAME_LANGUAGE } from '../../../../Utils/Constants';
import { Box } from '../../Components/Box/Box';
import { BoxSection } from '../../Components/BoxSection/BoxSection';
import { MenuItem } from '../../Components/Menu/Models/MenuItem';
import { NavigationBar } from '../../Components/NavigationBar/NavigationBar';
import { PageLayout } from '../../Components/PageLayout/PageLayout';
import { PrimaryButton } from '../../Components/PrimaryButton/PrimaryButton';
import { SearchFieldSuggestionItem } from '../../Components/SearchField/Models/SearchFieldSuggestionItem';
import { SearchField } from '../../Components/SearchField/SearchField';
import { Toggle } from '../../Components/Toggle/Toggle';
import { CreateMoonListPageMoonGrid } from './Sections/CreateMoonListPageMoonGrid';

export const CreateMoonListPage: FunctionComponent = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const name: string = useSelector(CreateMoonListSelectors.name);
  const isPublic: boolean = useSelector(CreateMoonListSelectors.isPublic);
  const search: string = useSelector(CreateMoonListSelectors.search);
  const searchLanguage: SmoLanguage = useSelector(CreateMoonListSelectors.searchLanguage);
  const searchResults: SmoMoon[] = useSelector(CreateMoonListSelectors.searchResults);
  const moons: SmoMoon[] = useSelector(CreateMoonListSelectors.moons);
  const canCreate: boolean = useSelector(CreateMoonListSelectors.canCreate);
  const hasCreatedSuccessfully = useSelector(CreateMoonListSelectors.hasCreatedSuccessfully);

  useEffect(() => {
    return (): void => {
      dispatch(CreateMoonListActions.resetState());
    };
  }, []);

  useEffect(() => {
    if (hasCreatedSuccessfully) {
      navigate('/moon/list');
    }
  }, [hasCreatedSuccessfully]);

  function createMoonList(): void {
    dispatch(CreateMoonListActions.createMoonList());
  }

  function didChangeName(event: ChangeEvent<HTMLInputElement>): void {
    dispatch(CreateMoonListActions.setName(event.target.value));
  }

  function didChangeVisibility(isPublicToggle: boolean): void {
    dispatch(CreateMoonListActions.setIsPublic(isPublicToggle));
  }

  function didSearchMoon(moonName: string): void {
    dispatch(CreateMoonListActions.setSearch(moonName));
    if (moonName.length === 0) {
      dispatch(CreateMoonListActions.setSearchResults([]));
    }
  }

  function didSelectMoonWithId(moonId: string): void {
    dispatch(CreateMoonListActions.selectMoonWithId(moonId));
  }

  function didChangeLanguage(language: string): void {
    dispatch(CreateMoonListActions.setSearchLanguage(language as SmoLanguage));
  }

  function menuItemForLanguage(language: SmoLanguage): MenuItem {
    return {
      id: language,
      item: language
    };
  }

  function suggestionItemForMoon(moon: SmoMoon): SearchFieldSuggestionItem {
    return {
      id: moon.id,
      item: moon.names[searchLanguage]
    };
  }

  return (
    <div className="pb-8">
      <NavigationBar title="New List">
        <PrimaryButton title="Create" disabled={!canCreate} onClick={createMoonList} />
      </NavigationBar>

      <PageLayout>
        <div className="pt-8 space-y-4">
          <Box>
            <BoxSection title="Name">
              <input
                className="w-full text-2xl font-medium placeholder-text-placeholder"
                value={name}
                placeholder="List name"
                onChange={didChangeName}
              />
            </BoxSection>
          </Box>

          <div className="items-stretch w-full space-x-4 h-stack">
            <Box className="flex-1">
              <BoxSection title="Moons">
                <h4>{moons.length}</h4>
              </BoxSection>
            </Box>
            <Box className="flex-1">
              <BoxSection title="Visibility">
                <div className="items-center justify-between h-stack">
                  <h4>Public</h4>
                  <Toggle isEnabled={isPublic} onChange={didChangeVisibility} />
                </div>
              </BoxSection>
            </Box>
          </div>

          <SearchField
            className="w-full py-2"
            placeholder="Search moons"
            value={search}
            menu={{
              items: allSmoLanguages.map(menuItemForLanguage),
              defaultValue: DEFAULT_GAME_LANGUAGE,
              onChange: didChangeLanguage
            }}
            suggestions={{
              items: searchResults.map(suggestionItemForMoon),
              onSelection: didSelectMoonWithId
            }}
            onSearch={didSearchMoon}
          />

          <CreateMoonListPageMoonGrid />
        </div>
      </PageLayout>
    </div>
  );
};
